import { useState } from "react";
import { Link, createRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Search } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { TabPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { ProductGrid } from "../components/product-grid.tsx";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCategories, useProducts } from "../api/queries.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopProducts } from "./desktop/products.tsx";

const SORTS = [
  { key: "newest", label: "最新" },
  { key: "sales", label: "销量" },
  { key: "price_asc", label: "价格↑" },
  { key: "price_desc", label: "价格↓" },
] as const;

interface ProductSearch {
  categoryId?: number;
  space?: string;
  keyword?: string;
  sort?: string;
}

/**
 * 🔴 **手机版和网页版是两套页面，不是同一份加断点。**
 *    信息结构本来就不一样：手机是"品类横滑条 + 两列瀑布流"，
 *    网页是"左侧固定筛选栏 + 右侧等高网格"。把手机那份撑到 1200 宽
 *    只是把页面拉长拉宽，该有的密度和并排比较一个都没有。
 * ⚠️ **URL 两边共用** —— 同一个地址在手机和电脑上都打得开，
 *    分享的链接、二维码、返回上一级全都不受影响。
 */
function ProductsRoute() {
  return useIsDesktop() ? <DesktopProducts /> : <Products />;
}

function Products() {
  const search = useSearch({ from: "/products" }) as ProductSearch;
  const navigate = useNavigate();
  const [kw, setKw] = useState(search.keyword ?? "");

  const { data: categories } = useCategories();
  const { data, isPending, error, refetch } = useProducts({
    categoryId: search.categoryId,
    space: search.space,
    keyword: search.keyword,
    sort: search.sort ?? "newest",
    limit: 20,
  });

  const setSearch = (next: Partial<ProductSearch>) =>
    navigate({ to: "/products", search: { ...search, ...next } as never });

  return (
    <TabPage>
      {/*
        ⚠️ 用 `pt-safe-4` 而不是 `pt-4 pt-safe` —— 后者两条都是 padding-top，
           没刘海的设备上安全区是 0、把 pt-4 覆盖成 0，搜索栏会贴死在顶边。
      */}
      <header className="sticky top-0 z-20 border-b border-border bg-background/95 px-3 pt-safe-4 pb-2 backdrop-blur-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch({ keyword: kw || undefined });
          }}
        >
          <div className="flex h-10 items-center gap-2 rounded-full bg-secondary px-4">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            {/* 字号 16px 是硬要求：iOS 上更小会让整页自动放大且缩不回来 */}
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜瓷砖、地板、灯具…"
              className="h-full min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground"
            />
          </div>
        </form>

        {/* 品类横滑。⚠️ 用 overflow-x-auto 而不是换行，否则品类一多就把整个首屏占满 */}
        {/* 品类横滑。用 overflow-x-auto 而不是换行，否则品类一多就把整个首屏占满 */}
        <div className="no-scrollbar -mx-3 mt-2.5 flex gap-2 overflow-x-auto px-3">
          {[{ id: undefined as number | undefined, name: "全部" }, ...(categories ?? [])].map((c) => {
            const active = search.categoryId === c.id;
            return (
              <button
                key={c.id ?? "all"}
                onClick={() => setSearch({ categoryId: c.id })}
                className={cn(
                  "shrink-0 rounded-full px-3.5 py-1.5 text-[13px] whitespace-nowrap transition-colors",
                  active
                    ? "bg-primary font-medium text-primary-foreground"
                    : "bg-secondary text-muted-foreground",
                )}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        {/*
          排序做成一整条分段控件，而不是四个裸文字 ——
          裸文字看不出"这是一组互斥选项"，用户不知道点了会怎样。
        */}
        <div className="mt-2 flex rounded-lg bg-secondary p-0.5">
          {SORTS.map((s) => {
            const active = (search.sort ?? "newest") === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setSearch({ sort: s.key })}
                className={cn(
                  "flex-1 rounded-md py-1.5 text-[13px] transition-colors",
                  active
                    ? "bg-background font-medium text-foreground shadow-sm"
                    : "text-muted-foreground",
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </header>
      {isPending ? (
        <Spinner rows={4} />
      ) : error ? (
        <ErrorBlock error={error} onRetry={() => refetch()} />
      ) : data.items.length === 0 ? (
        <Empty title="没找到符合条件的商品" hint="换个品类或关键词试试" />
      ) : (
        <div className="p-3">
          <ProductGrid items={data.items} />
        </div>
      )}
    </TabPage>
  );
}

export const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: ProductsRoute,
  validateSearch: (s: Record<string, unknown>): ProductSearch => ({
    categoryId: s.categoryId ? Number(s.categoryId) : undefined,
    space: typeof s.space === "string" ? s.space : undefined,
    keyword: typeof s.keyword === "string" ? s.keyword : undefined,
    sort: typeof s.sort === "string" ? s.sort : undefined,
  }),
});
